import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  checkUsageLimit,
  incrementUsage,
} from "../../services/operations/chatbotAPI";
import { fetchCurriculumByClass } from "../../services/operations/curriculumAPI";
import { ObjectId } from "bson";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// --- VOICE ASSISTANT CONFIGURATION
const BASE_URL =
  process.env.REACT_APP_VOICE_TO_VOICE_URL || "ws://localhost:8000";
const WS_URL = `${BASE_URL.replace(/^http/, "ws")}/voice-chat`;

const REQUESTED_UPLINK_SAMPLE_RATE = 16000;
const DEFAULT_TTS_SAMPLE_RATE = 24000;
const PLAYBACK_BUFFER_TARGET_SECONDS = 0.2;

// --- IMPROVED: VOICE ACTIVITY DETECTION (VAD) CONFIGURATION ---
const IMPROVED_VAD_CONFIG = {
  VOICE_ACTIVITY_THRESHOLD: 0.03,
  SILENCE_DURATION_MS: 600,
  ENERGY_CONSISTENCY_WINDOW: 5,
  MIN_ENERGY_RATIO: 0.65,
  SPECTRAL_ROLLOFF_THRESHOLD: 0.88,
  ZCR_THRESHOLD: 0.3,
  SPIKE_THRESHOLD: 1.8,
  CONFIDENCE_THRESHOLD: 0.75,
};

// ============================================================================
// --- WHITEBOARD RENDERER (handwriting + stroke-drawn shapes) ---
// ============================================================================
const HAND_FONT = "'Comic Neue', cursive";

const HandwrittenText = ({ text }) => {
  const chars = Array.from(text);
  return (
    <motion.div
      key={text}
      className="text-4xl md:text-5xl text-slate-800 font-bold leading-snug px-2 break-words"
      style={{ fontFamily: HAND_FONT }}
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
    >
      {chars.map((ch, i) => (
        <motion.span
          key={i}
          className="inline-block"
          // Add whiteSpace: "pre" to force the browser to render the space
          style={{ whiteSpace: "pre" }}
          variants={{
            hidden: { opacity: 0, y: 6 },
            visible: { opacity: 1, y: 0 },
          }}
        >
          {/* Safely fallback to a non-breaking space unicode character */}
          {ch === " " ? "\u00A0" : ch}
        </motion.span>
      ))}
    </motion.div>
  );
};

const SHAPES = {
  triangle: {
    kind: "polygon",
    color: "#c084fc",
    w: 190,
    h: 170,
    points: [
      [95, 20],
      [20, 150],
      [170, 150],
    ],
  },
  square: {
    kind: "polygon",
    color: "#34d399",
    w: 170,
    h: 170,
    points: [
      [30, 30],
      [140, 30],
      [140, 140],
      [30, 140],
    ],
  },
  rectangle: {
    kind: "polygon",
    color: "#fbbf24",
    w: 210,
    h: 145,
    points: [
      [20, 30],
      [190, 30],
      [190, 115],
      [20, 115],
    ],
  },
  quadrilateral: {
    kind: "polygon",
    color: "#f472b6",
    w: 210,
    h: 160,
    points: [
      [45, 30],
      [175, 30],
      [150, 130],
      [20, 130],
    ],
  },
  line: {
    kind: "line",
    color: "#1e293b",
    w: 210,
    h: 40,
    points: [
      [12, 20],
      [198, 20],
    ],
  },
  circle: {
    kind: "circle",
    color: "#60a5fa",
    w: 170,
    h: 170,
    cx: 85,
    cy: 85,
    r: 65,
  },
  angle: {
    kind: "angle",
    color: "#c084fc",
    w: 200,
    h: 150,
    v: [38, 122],
    a: [188, 122],
    b: [120, 20],
  },
  right_angle: {
    kind: "angle",
    color: "#c084fc",
    w: 180,
    h: 150,
    v: [38, 122],
    a: [182, 122],
    b: [38, 18],
  },
  cube: { kind: "solid", color: "#c084fc", w: 190, h: 175 },
  cuboid: { kind: "solid", color: "#c084fc", w: 210, h: 165 },
  sphere: { kind: "solid", color: "#60a5fa", w: 185, h: 185 },
  cylinder: { kind: "solid", color: "#34d399", w: 185, h: 185 },
  cone: { kind: "solid", color: "#fbbf24", w: 185, h: 175 },
  pyramid: { kind: "solid", color: "#f472b6", w: 190, h: 175 },
};
const SHAPE_ALIASES = {
  right_angle_triangle: "triangle",
  surface: "rectangle",
  "right angle": "right_angle",
  ray: "line",
  box: "cuboid",
  ball: "sphere",
};
const HIGHLIGHT_COLOR = "#ef4444";

const sub = (a, b) => [a[0] - b[0], a[1] - b[1]];
const add = (a, b) => [a[0] + b[0], a[1] + b[1]];
const scale = (a, s) => [a[0] * s, a[1] * s];
const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const unit = (a) => {
  const m = Math.hypot(a[0], a[1]) || 1;
  return [a[0] / m, a[1] / m];
};
const centroidOf = (pts) => {
  const s = pts.reduce((acc, p) => [acc[0] + p[0], acc[1] + p[1]], [0, 0]);
  return [s[0] / pts.length, s[1] / pts.length];
};
const away = (p, c, d) => add(p, scale(unit(sub(p, c)), d));
const towards = (p, c, d) => add(p, scale(unit(sub(c, p)), d));
const interiorAngle = (P, V, N) => {
  const a = unit(sub(P, V));
  const b = unit(sub(N, V));
  const dot = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1]));
  return Math.round((Math.acos(dot) * 180) / Math.PI);
};
const sideLabelsFor = (name, points, closed) => {
  const n = points.length;
  const edges = closed ? n : n - 1;
  const lengths = [];
  for (let i = 0; i < edges; i++)
    lengths.push(dist(points[i], points[(i + 1) % n]));
  if (name === "square") return lengths.map(() => "s");
  if (name === "rectangle") {
    const maxL = Math.max(...lengths);
    return lengths.map((l) => (Math.abs(l - maxL) < 1 ? "l" : "b"));
  }
  if (name === "line") return ["length"];
  return lengths.map((_, i) => String.fromCharCode(97 + i));
};

const Label = ({ x, y, children, size = 15, delay = 0 }) => (
  <motion.text
    x={x}
    y={y}
    fill={HIGHLIGHT_COLOR}
    fontSize={size}
    fontWeight="bold"
    textAnchor="middle"
    dominantBaseline="middle"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay }}
  >
    {children}
  </motion.text>
);

const VertexMarks = ({ points, center }) =>
  points.map(([x, y], i) => {
    const [lx, ly] = away([x, y], center, 16);
    return (
      <g key={i}>
        <motion.circle
          cx={x}
          cy={y}
          r="6"
          fill={HIGHLIGHT_COLOR}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            delay: 0.08 + i * 0.1,
            type: "spring",
            stiffness: 320,
            damping: 18,
          }}
        />
        <Label x={lx} y={ly} size={16} delay={0.18 + i * 0.1}>
          {String.fromCharCode(65 + i)}
        </Label>
      </g>
    );
  });

const AngleMarks = ({ points, closed, center, size = 18 }) => {
  const n = points.length;
  const marks = [];
  points.forEach((V, i) => {
    if (!closed && (i === 0 || i === n - 1)) return;
    const P = points[(i - 1 + n) % n];
    const N = points[(i + 1) % n];
    const a = add(V, scale(unit(sub(P, V)), size));
    const c = add(V, scale(unit(sub(N, V)), size));
    const corner = add(a, scale(unit(sub(N, V)), size));
    const [tx, ty] = towards(V, center, 32);
    marks.push(
      <g key={i}>
        <motion.path
          d={`M ${a[0]} ${a[1]} L ${corner[0]} ${corner[1]} L ${c[0]} ${c[1]}`}
          fill="none"
          stroke={HIGHLIGHT_COLOR}
          strokeWidth="2.5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.08 + i * 0.1 }}
        />
        <Label x={tx} y={ty} size={14} delay={0.18 + i * 0.1}>
          {interiorAngle(P, V, N)}°
        </Label>
      </g>,
    );
  });
  return marks;
};

const SideMarks = ({ points, closed, center, labels }) => {
  const n = points.length;
  const edges = closed ? n : n - 1;
  const marks = [];
  for (let i = 0; i < edges; i++) {
    const A = points[i];
    const B = points[(i + 1) % n];
    const mid = [(A[0] + B[0]) / 2, (A[1] + B[1]) / 2];
    const [lx, ly] = away(mid, center, 16);
    marks.push(
      <g key={i}>
        <motion.line
          x1={A[0]}
          y1={A[1]}
          x2={B[0]}
          y2={B[1]}
          stroke={HIGHLIGHT_COLOR}
          strokeWidth="5"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ delay: 0.08 + i * 0.12, duration: 0.45 }}
        />
        {labels[i] && (
          <Label x={lx} y={ly} delay={0.18 + i * 0.12}>
            {labels[i]}
          </Label>
        )}
      </g>,
    );
  }
  return marks;
};

const CircleMarks = ({ cx, cy, r, highlight }) => {
  const lineDraw = {
    initial: { pathLength: 0 },
    animate: { pathLength: 1 },
    transition: { delay: 0.1, duration: 0.5 },
  };
  const centerDot = (
    <motion.circle
      cx={cx}
      cy={cy}
      r="5"
      fill={HIGHLIGHT_COLOR}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.05, type: "spring", stiffness: 320, damping: 18 }}
    />
  );
  switch (highlight) {
    case "center":
      return (
        <g>
          {centerDot}
          <Label x={cx} y={cy - 14} delay={0.25}>
            center
          </Label>
        </g>
      );
    case "radius":
      return (
        <g>
          {centerDot}
          <motion.line
            x1={cx}
            y1={cy}
            x2={cx + r}
            y2={cy}
            stroke={HIGHLIGHT_COLOR}
            strokeWidth="3"
            {...lineDraw}
          />
          <Label x={cx + r / 2} y={cy - 12} delay={0.35}>
            r
          </Label>
        </g>
      );
    case "diameter":
      return (
        <g>
          {centerDot}
          <motion.line
            x1={cx - r}
            y1={cy}
            x2={cx + r}
            y2={cy}
            stroke={HIGHLIGHT_COLOR}
            strokeWidth="3"
            {...lineDraw}
          />
          <Label x={cx} y={cy - 12} delay={0.35}>
            d
          </Label>
        </g>
      );
    case "circumference":
      return (
        <motion.circle
          cx={cx}
          cy={cy}
          r={r}
          fill="transparent"
          stroke={HIGHLIGHT_COLOR}
          strokeWidth="5"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
        />
      );
    default:
      return null;
  }
};

const AngleFigure = ({ shape }) => {
  const { v, a, b, color } = shape;
  const ray = (to, delay) => (
    <motion.line
      x1={v[0]}
      y1={v[1]}
      x2={to[0]}
      y2={to[1]}
      stroke={color}
      strokeWidth="4"
      strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ delay, duration: 0.45, ease: "easeInOut" }}
    />
  );
  const R = 34;
  const angA = Math.atan2(a[1] - v[1], a[0] - v[0]);
  const angB = Math.atan2(b[1] - v[1], b[0] - v[0]);
  let d = "";
  const steps = 18;
  for (let i = 0; i <= steps; i++) {
    const ang = angA + (angB - angA) * (i / steps);
    d += `${i === 0 ? "M" : "L"} ${(v[0] + R * Math.cos(ang)).toFixed(1)} ${(v[1] + R * Math.sin(ang)).toFixed(1)} `;
  }
  const deg = interiorAngle(a, v, b);
  const bis = unit(add(unit(sub(a, v)), unit(sub(b, v))));
  const [lx, ly] = add(v, scale(bis, R + 24));
  return (
    <g>
      {ray(a, 0)}
      {ray(b, 0.25)}
      <motion.path
        d={d}
        fill="none"
        stroke={HIGHLIGHT_COLOR}
        strokeWidth="2.5"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ delay: 0.45, duration: 0.4 }}
      />
      <Label x={lx} y={ly} size={15} delay={0.6}>
        {deg}°
      </Label>
    </g>
  );
};

const Solid3D = ({ name, color }) => {
  const common = {
    fill: "transparent",
    stroke: color,
    strokeWidth: 3.5,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  const hidden = {
    fill: "transparent",
    stroke: color,
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeDasharray: "4 4",
    opacity: 0.55,
  };
  const draw = (delay = 0) => ({
    initial: { pathLength: 0, opacity: 0 },
    animate: { pathLength: 1, opacity: 1 },
    transition: { delay, duration: 0.6, ease: "easeInOut" },
  });

  switch (name) {
    case "cube":
    case "cuboid": {
      const fx = 38,
        fy = 62,
        fw = name === "cuboid" ? 108 : 84,
        fh = 84,
        dx = 34,
        dy = -30;
      const A = [fx, fy],
        B = [fx + fw, fy],
        C = [fx + fw, fy + fh],
        D = [fx, fy + fh];
      const A2 = [A[0] + dx, A[1] + dy],
        B2 = [B[0] + dx, B[1] + dy],
        C2 = [C[0] + dx, C[1] + dy],
        D2 = [D[0] + dx, D[1] + dy];
      return (
        <g>
          <motion.path
            d={`M ${A2} L ${B2} L ${C2} L ${D2} Z`}
            {...hidden}
            {...draw(0.25)}
          />
          <motion.line
            x1={A[0]}
            y1={A[1]}
            x2={A2[0]}
            y2={A2[1]}
            {...common}
            {...draw(0.35)}
          />
          <motion.line
            x1={B[0]}
            y1={B[1]}
            x2={B2[0]}
            y2={B2[1]}
            {...common}
            {...draw(0.35)}
          />
          <motion.line
            x1={C[0]}
            y1={C[1]}
            x2={C2[0]}
            y2={C2[1]}
            {...common}
            {...draw(0.35)}
          />
          <motion.line
            x1={D[0]}
            y1={D[1]}
            x2={D2[0]}
            y2={D2[1]}
            {...common}
            {...draw(0.35)}
          />
          <motion.path
            d={`M ${A} L ${B} L ${C} L ${D} Z`}
            {...common}
            {...draw(0)}
          />
        </g>
      );
    }
    case "sphere":
      return (
        <g>
          <motion.circle cx="92" cy="92" r="62" {...common} {...draw(0)} />
          <motion.ellipse
            cx="92"
            cy="92"
            rx="62"
            ry="18"
            {...common}
            {...draw(0.35)}
          />
        </g>
      );
    case "cylinder":
      return (
        <g>
          <motion.ellipse
            cx="92"
            cy="46"
            rx="50"
            ry="15"
            {...common}
            {...draw(0)}
          />
          <motion.line
            x1="42"
            y1="46"
            x2="42"
            y2="142"
            {...common}
            {...draw(0.25)}
          />
          <motion.line
            x1="142"
            y1="46"
            x2="142"
            y2="142"
            {...common}
            {...draw(0.25)}
          />
          <motion.ellipse
            cx="92"
            cy="142"
            rx="50"
            ry="15"
            {...common}
            {...draw(0.4)}
          />
        </g>
      );
    case "cone":
      return (
        <g>
          <motion.line
            x1="92"
            y1="24"
            x2="42"
            y2="142"
            {...common}
            {...draw(0)}
          />
          <motion.line
            x1="92"
            y1="24"
            x2="142"
            y2="142"
            {...common}
            {...draw(0.15)}
          />
          <motion.ellipse
            cx="92"
            cy="142"
            rx="50"
            ry="15"
            {...common}
            {...draw(0.35)}
          />
        </g>
      );
    case "pyramid": {
      const apex = [92, 24],
        bl = [36, 142],
        br = [148, 142],
        bb = [116, 112],
        bt = [64, 112];
      return (
        <g>
          <motion.path d={`M ${bt} L ${bb}`} {...hidden} {...draw(0.3)} />
          <motion.line
            x1={apex[0]}
            y1={apex[1]}
            x2={bl[0]}
            y2={bl[1]}
            {...common}
            {...draw(0)}
          />
          <motion.line
            x1={apex[0]}
            y1={apex[1]}
            x2={br[0]}
            y2={br[1]}
            {...common}
            {...draw(0.12)}
          />
          <motion.line
            x1={apex[0]}
            y1={apex[1]}
            x2={bb[0]}
            y2={bb[1]}
            {...common}
            {...draw(0.2)}
          />
          <motion.line
            x1={apex[0]}
            y1={apex[1]}
            x2={bt[0]}
            y2={bt[1]}
            {...hidden}
            {...draw(0.3)}
          />
          <motion.path
            d={`M ${bl} L ${br} L ${bb}`}
            {...common}
            {...draw(0.15)}
          />
        </g>
      );
    }
    default:
      return null;
  }
};

const DrawnShape = ({ object, highlight }) => {
  const name = SHAPE_ALIASES[object] || object;
  const shape = SHAPES[name];
  if (!shape) return <HandwrittenText text={object} />;

  if (shape.kind === "angle") {
    return (
      <motion.div
        className="mx-auto"
        style={{ originX: 0.5, originY: 0.5 }}
        animate={{ scale: highlight ? 1.12 : 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 18 }}
      >
        <svg
          width={shape.w}
          height={shape.h}
          className="mx-auto overflow-visible"
        >
          <AngleFigure shape={shape} />
        </svg>
      </motion.div>
    );
  }

  if (shape.kind === "solid") {
    return (
      <motion.div
        className="mx-auto"
        style={{ originX: 0.5, originY: 0.5 }}
        animate={{ scale: highlight ? 1.12 : 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 18 }}
      >
        <svg
          width={shape.w}
          height={shape.h}
          className="mx-auto overflow-visible"
        >
          <Solid3D name={name} color={shape.color} />
        </svg>
      </motion.div>
    );
  }

  const draw = {
    initial: { pathLength: 0, opacity: 0 },
    animate: { pathLength: 1, opacity: 1 },
    transition: { duration: 1, ease: "easeInOut" },
  };
  const common = {
    fill: "transparent",
    strokeWidth: 4,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  const closed = shape.kind === "polygon";

  let outline;
  if (shape.kind === "circle") {
    outline = (
      <motion.circle
        cx={shape.cx}
        cy={shape.cy}
        r={shape.r}
        stroke={shape.color}
        {...common}
        {...draw}
      />
    );
  } else {
    const [first, ...rest] = shape.points;
    const d =
      `M ${first[0]} ${first[1]} ` +
      rest.map((p) => `L ${p[0]} ${p[1]}`).join(" ") +
      (closed ? " Z" : "");
    outline = <motion.path d={d} stroke={shape.color} {...common} {...draw} />;
  }

  let overlay = null;
  if (highlight && shape.kind === "circle") {
    overlay = (
      <CircleMarks
        cx={shape.cx}
        cy={shape.cy}
        r={shape.r}
        highlight={highlight}
      />
    );
  } else if (highlight && shape.points) {
    const center = centroidOf(shape.points);
    if (highlight === "vertices" || highlight === "corners")
      overlay = <VertexMarks points={shape.points} center={center} />;
    else if (highlight === "angles")
      overlay = (
        <AngleMarks points={shape.points} closed={closed} center={center} />
      );
    else if (highlight === "sides" || highlight === "edges")
      overlay = (
        <SideMarks
          points={shape.points}
          closed={closed}
          center={center}
          labels={sideLabelsFor(name, shape.points, closed)}
        />
      );
  }

  return (
    <motion.div
      className="mx-auto"
      style={{ originX: 0.5, originY: 0.5 }}
      animate={{ scale: highlight ? 1.18 : 1 }}
      transition={{ type: "spring", stiffness: 200, damping: 18 }}
    >
      <svg
        width={shape.w}
        height={shape.h}
        className="mx-auto overflow-visible"
      >
        {outline}
        {overlay}
      </svg>
    </motion.div>
  );
};

const SCENE_STROKE = "#2563eb";
const SCENE_INK = "#0f172a";
const ATOM_ARGS = {
  dot: 2,
  circle: 3,
  line: 4,
  arrow: 4,
  rect: 4,
  label: 2,
  icon: 2,
};

const ICON_MAP = {
  person: "mdi:account",
  people: "mdi:account-group",
  teacher: "mdi:human-male-board",
  student: "mdi:school",
  book: "mdi:book-open-page-variant",
  pencil: "mdi:pencil",
  brain: "mdi:brain",
  idea: "mdi:lightbulb-on",
  question: "mdi:help-circle",
  robot: "mdi:robot",
  atom: "mdi:atom",
  magnet: "mdi:magnet",
  battery: "mdi:battery",
  bulb: "mdi:lightbulb",
  light: "mdi:white-balance-sunny",
  energy: "mdi:lightning-bolt",
  force: "mdi:arrow-right-bold",
  gear: "mdi:cog",
  thermometer: "mdi:thermometer",
  ruler: "mdi:ruler",
  scale: "mdi:scale-balance",
  speed: "mdi:speedometer",
  sound: "mdi:volume-high",
  wave: "mdi:sine-wave",
  mirror: "mdi:mirror",
  rocket: "mdi:rocket",
  clock: "mdi:clock-outline",
  weight: "mdi:weight",
  flask: "mdi:flask",
  testtube: "mdi:test-tube",
  beaker: "mdi:beaker",
  molecule: "mdi:molecule",
  fire: "mdi:fire",
  water: "mdi:water",
  drop: "mdi:water",
  gas: "mdi:cloud",
  snowflake: "mdi:snowflake",
  microscope: "mdi:microscope",
  dna: "mdi:dna",
  leaf: "mdi:leaf",
  tree: "mdi:tree",
  flower: "mdi:flower",
  plant: "mdi:sprout",
  heart: "mdi:heart",
  lungs: "mdi:lungs",
  eye: "mdi:eye",
  bone: "mdi:bone",
  tooth: "mdi:tooth",
  bacteria: "mdi:bacteria",
  virus: "mdi:virus",
  human: "mdi:human",
  animal: "mdi:paw",
  bird: "mdi:bird",
  fish: "mdi:fish",
  dog: "mdi:dog",
  cat: "mdi:cat",
  cow: "mdi:cow",
  bug: "mdi:bug",
  apple: "mdi:food-apple",
  food: "mdi:food",
  ear: "mdi:ear-hearing",
  earth: "mdi:earth",
  globe: "mdi:earth",
  sun: "mdi:white-balance-sunny",
  moon: "mdi:moon-waning-crescent",
  star: "mdi:star",
  cloud: "mdi:cloud",
  rain: "mdi:weather-rainy",
  wind: "mdi:weather-windy",
  mountain: "mdi:image-filter-hdr",
  volcano: "mdi:volcano",
  map: "mdi:map",
  flag: "mdi:flag",
  compass: "mdi:compass",
  home: "mdi:home",
  city: "mdi:city",
  factory: "mdi:factory",
  money: "mdi:cash",
  bank: "mdi:bank",
  justice: "mdi:scale-balance",
  crown: "mdi:crown",
  calendar: "mdi:calendar",
  train: "mdi:train",
  bus: "mdi:bus",
  car: "mdi:car",
  plane: "mdi:airplane",
  ship: "mdi:ferry",
  road: "mdi:road-variant",
  calculator: "mdi:calculator",
  graph: "mdi:chart-line",
  plus: "mdi:plus",
  minus: "mdi:minus",
  equal: "mdi:equal",
  percent: "mdi:percent",
  angle: "mdi:angle-acute",
  dice: "mdi:dice-5",
  coin: "mdi:cash",
  arrow: "mdi:arrow-right-bold",
  up: "mdi:arrow-up-bold",
  down: "mdi:arrow-down-bold",
  check: "mdi:check-bold",
  cross: "mdi:close-thick",
};
const iconifyUrl = (id) =>
  `https://api.iconify.design/${id.split(":")[0]}/${id.split(":")[1]}.svg?color=%231e293b`;
const iconCache = new Map();
const resolveIconId = async (name) => {
  if (ICON_MAP[name]) return ICON_MAP[name];
  if (iconCache.has(name)) return iconCache.get(name);
  try {
    const res = await fetch(
      `https://api.iconify.design/search?query=${encodeURIComponent(name)}&limit=1`,
    );
    const data = await res.json();
    const id = (data && data.icons && data.icons[0]) || null;
    iconCache.set(name, id);
    return id;
  } catch {
    iconCache.set(name, null);
    return null;
  }
};

const parseScene = (spec) =>
  spec
    .split(";")
    .map((raw) => {
      const s = raw.trim();
      if (!s) return null;
      const labelMatch = s.match(/"([^"]*)"/);
      const label = labelMatch ? labelMatch[1] : null;
      const parts = s
        .replace(/"[^"]*"/, "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      const type = (parts.shift() || "").toLowerCase();
      if (!(type in ATOM_ARGS)) return null;
      const name = type === "icon" ? (parts.shift() || "").toLowerCase() : null;
      const n = parts.map(Number);
      if (n.length < ATOM_ARGS[type] || n.some(Number.isNaN)) return null;
      if (type === "label" && !label) return null;
      if (type === "icon" && !name) return null;
      return { type, name, n, label };
    })
    .filter(Boolean);

const IconAtom = ({ name, x, y, size, label, delay }) => {
  const [iconId, setIconId] = useState(() => ICON_MAP[name] || null);
  const [done, setDone] = useState(() => !!ICON_MAP[name]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (ICON_MAP[name]) return;
    let cancelled = false;
    resolveIconId(name).then((id) => {
      if (cancelled) return;
      setIconId(id);
      setDone(true);
    });
    return () => {
      cancelled = true;
    };
  }, [name]);

  const url = iconId ? iconifyUrl(iconId) : null;
  if (failed || (done && !url)) {
    return (
      <motion.text
        x={x}
        y={y}
        fill={SCENE_INK}
        fontSize="6"
        fontWeight="bold"
        textAnchor="middle"
        dominantBaseline="middle"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay }}
      >
        {label || name}
      </motion.text>
    );
  }
  if (!url) return null;
  return (
    <g>
      <motion.image
        href={url}
        x={x - size / 2}
        y={y - size / 2}
        width={size}
        height={size}
        onError={() => setFailed(true)}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay, type: "spring", stiffness: 260, damping: 20 }}
        style={{ transformBox: "fill-box", transformOrigin: "center" }}
      />
      {label && (
        <motion.text
          x={x}
          y={y + size / 2 + 6}
          fill={SCENE_INK}
          fontSize="5"
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="middle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: delay + 0.15 }}
        >
          {label}
        </motion.text>
      )}
    </g>
  );
};

const SceneAtom = ({ atom, i }) => {
  const { type, n, label, name } = atom;
  const delay = 0.05 + i * 0.08;
  const draw = {
    initial: { pathLength: 0, opacity: 0 },
    animate: { pathLength: 1, opacity: 1 },
    transition: { delay, duration: 0.5, ease: "easeInOut" },
  };
  const stroke = {
    fill: "transparent",
    stroke: SCENE_STROKE,
    strokeWidth: 1.4,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  const text = (x, y, size = 5.5, extra = 0.15) =>
    label ? (
      <motion.text
        x={x}
        y={y}
        fill={SCENE_INK}
        fontSize={size}
        fontWeight="bold"
        textAnchor="middle"
        dominantBaseline="middle"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: delay + extra }}
      >
        {label}
      </motion.text>
    ) : null;

  switch (type) {
    case "dot":
      return (
        <g>
          <motion.circle
            cx={n[0]}
            cy={n[1]}
            r="2"
            fill={SCENE_STROKE}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay, type: "spring", stiffness: 320, damping: 18 }}
          />
          {text(n[0], n[1] - 4)}
        </g>
      );
    case "circle":
      return (
        <g>
          <motion.circle cx={n[0]} cy={n[1]} r={n[2]} {...stroke} {...draw} />
          {text(n[0], n[1])}
        </g>
      );
    case "line":
      return (
        <motion.line
          x1={n[0]}
          y1={n[1]}
          x2={n[2]}
          y2={n[3]}
          {...stroke}
          {...draw}
        />
      );
    case "arrow": {
      const len = Math.hypot(n[2] - n[0], n[3] - n[1]) || 1;
      const ux = (n[2] - n[0]) / len,
        uy = (n[3] - n[1]) / len;
      const bx = n[2] - ux * 3.5,
        by = n[3] - uy * 3.5;
      return (
        <g>
          <motion.line
            x1={n[0]}
            y1={n[1]}
            x2={n[2]}
            y2={n[3]}
            {...stroke}
            {...draw}
          />
          <motion.path
            d={`M ${n[2]} ${n[3]} L ${bx + -uy * 3.5 * 0.6} ${by + ux * 3.5 * 0.6} L ${bx - -uy * 3.5 * 0.6} ${by - ux * 3.5 * 0.6} Z`}
            fill={SCENE_STROKE}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: delay + 0.35 }}
          />
          {text((n[0] + n[2]) / 2, (n[1] + n[3]) / 2 - 3)}
        </g>
      );
    }
    case "rect":
      return (
        <g>
          <motion.rect
            x={n[0]}
            y={n[1]}
            width={n[2]}
            height={n[3]}
            {...stroke}
            {...draw}
          />
          {text(n[0] + n[2] / 2, n[1] + n[3] / 2)}
        </g>
      );
    case "label":
      return (
        <motion.text
          x={n[0]}
          y={n[1]}
          fill={SCENE_INK}
          fontSize="6"
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="middle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay }}
        >
          {label}
        </motion.text>
      );
    case "icon":
      return (
        <IconAtom
          name={name}
          x={n[0]}
          y={n[1]}
          size={n[2] || 22}
          label={label}
          delay={delay}
        />
      );
    default:
      return null;
  }
};

const SceneRenderer = ({ spec }) => {
  const atoms = parseScene(spec);
  if (atoms.length === 0) return <HandwrittenText text={spec} />;
  return (
    <svg
      viewBox="0 0 100 100"
      width="270"
      height="270"
      className="mx-auto overflow-visible"
    >
      {atoms.map((atom, i) => (
        <SceneAtom key={i} atom={atom} i={i} />
      ))}
    </svg>
  );
};

const classifyTag = (tag) => {
  if (!tag || tag === "CLEAR") return { action: "clear" };
  const cleanTag = tag.replace(/^\[/, "").replace(/\]$/, "");
  const idx = cleanTag.indexOf(":");
  if (idx === -1) return { action: "write", payload: cleanTag.trim() };
  const action = cleanTag.slice(0, idx).trim().toLowerCase();
  const payload = cleanTag.slice(idx + 1).trim();
  if (action === "scene") return { action: "scene", payload };
  if (action === "draw")
    return { action: "draw", payload: payload.toLowerCase() };
  if (action === "highlight")
    return { action: "highlight", payload: payload.toLowerCase() };
  const clean = payload
    .split(/;|\bWRITE:|\bDRAW:|\bSCENE:|\bHIGHLIGHT:/i)[0]
    .trim();
  return { action: "write", payload: clean || payload };
};

// ============================================================================
// --- MAIN APPLICATION COMPONENT ---
// ============================================================================

const AITutorPage = () => {
  const { t, i18n } = useTranslation();

  // --- UI State ---
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  // Track mic mute state
  const [isMuted, setIsMuted] = useState(false);

  // --- Redux State ---
  const user = useSelector((state) => state.profile?.user);
  const dispatch = useDispatch();

  // --- Context Selection State ---
  const [language, setLanguage] = useState("en-US");
  const className = useState(user?.class);

  // --- Voice Assistant State ---
  const [isRecording, setIsRecording] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAIResponding, setIsAIResponding] = useState(false);

  const socketRef = useRef(null);
  const inputAudioContextRef = useRef(null);
  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const inputAudioWorkletNodeRef = useRef(null);
  const isPlayingRef = useRef(false);
  const currentAudioSourceRef = useRef(null);
  const playbackQueueRef = useRef([]);
  const nextStartTimeRef = useRef(0);
  const isAIRespondingRef = useRef(false);
  const audioStreamEndedRef = useRef(false);

  // --- AEC FIX: Hidden Audio Element Ref ---
  const outputAudioElementRef = useRef(null);

  // --- WHITEBOARD SYNC REFS & STATE ---
  const pendingAnimationsRef = useRef([]);
  const scheduledAnimationsRef = useRef([]);
  const animationFrameRef = useRef(null);
  const [boardShape, setBoardShape] = useState(null);
  const [boardText, setBoardText] = useState(null);
  const [boardHighlight, setBoardHighlight] = useState(null);
  const [boardScene, setBoardScene] = useState(null);

  // --- VAD Refs ---
  const voiceActivityRef = useRef(false);
  const silenceTimeoutRef = useRef(null);
  const energyHistoryRef = useRef([]);
  const backgroundNoiseRef = useRef(0.001);
  const speechConfidenceRef = useRef(0);
  const endOfTurnTimerRef = useRef(null);
  const navigate = useNavigate();

  // --- Subject/Chapter State ---
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [curriculumData, setCurriculumData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);


  // --- WHITEBOARD SYNCHRONIZATION ENGINE ---
  useEffect(() => {
    const syncAnimations = () => {
      if (audioCtxRef.current && audioCtxRef.current.state === "running") {
        const now = audioCtxRef.current.currentTime;
        const dueIndex = scheduledAnimationsRef.current.findIndex(
          (anim) => now >= anim.executeAt,
        );

        if (dueIndex !== -1) {
          const dueAnimations = scheduledAnimationsRef.current.splice(
            0,
            dueIndex + 1,
          );
          dueAnimations.forEach((anim) => {
            console.log(
              `[SYNC] Triggering animation: ${anim.tag} at ${now.toFixed(3)}s`,
            );
            const { action, payload } = classifyTag(anim.tag);
            if (action === "clear") {
              setBoardShape(null);
              setBoardText(null);
              setBoardHighlight(null);
              setBoardScene(null);
            } else if (action === "scene") {
              setBoardScene((prev) => (prev ? prev + "; " + payload : payload));
              setBoardShape(null);
              setBoardHighlight(null);
            } else if (action === "draw") {
              setBoardShape(payload);
              setBoardText(null);
              setBoardHighlight(null);
              setBoardScene(null);
            } else if (action === "highlight") {
              setBoardHighlight(payload);
              setBoardText(payload);
            } else {
              setBoardText(payload);
            }
          });
        }
      }
      animationFrameRef.current = requestAnimationFrame(syncAnimations);
    };

    animationFrameRef.current = requestAnimationFrame(syncAnimations);
    return () => {
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  useEffect(() => {
    const loadCurriculum = async () => {
      if (!user?.class) {
        return;
      }
      try {
        const data = await dispatch(fetchCurriculumByClass());
        if (data) {
          setCurriculumData(data);
          if (data.subjects) {
            const subjectList = Object.keys(data.subjects).map((key) => ({
              id: key,
              name:
                key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
            }));
            setSubjects(subjectList);
          }
        }
      } catch (err) {
        console.error("Failed to load curriculum:", err);
      }
    };
    loadCurriculum();
  }, [user?.class, dispatch]);

  useEffect(() => {
    if (
      selectedSubject &&
      curriculumData?.subjects?.[selectedSubject]?.chapters
    ) {
      const chapterList = curriculumData.subjects[selectedSubject].chapters;
      setChapters(chapterList);
    } else {
      setChapters([]);
    }
  }, [selectedSubject, curriculumData]);

  const serverFormatRef = useRef({
    codec: "pcm_s16le",
    sampleRate: DEFAULT_TTS_SAMPLE_RATE,
    channels: 1,
  });

  const masterGainRef = useRef(null);
  const compressorRef = useRef(null);
  const filterRef = useRef(null);
  const bufferedDurationRef = useRef(0);

  const processingMessages = [
    t("voicebot.gettingReady"),
    t("voicebot.loadingFunFacts"),
    t("voicebot.settingUpAdventure"),
    t("voicebot.tutorReady"),
  ];


  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingMessageIndex((prevIndex) => {
        if (prevIndex === processingMessages.length - 1) {
          clearInterval(interval);
          setTimeout(() => {
            setIsLoading(false);
          }, 500);
          return prevIndex;
        }
        return prevIndex + 1;
      });
    }, 700);
    return () => {
      clearInterval(interval);
    };
  }, [processingMessages.length]);

  // --- CORE AUDIO LOGIC WITH AEC FIX ---

  const ensurePlaybackAudioContext = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
      try {
        audioCtxRef.current = new (
          window.AudioContext || window.webkitAudioContext
        )({
          sampleRate: serverFormatRef.current.sampleRate,
          latencyHint: "interactive",
        });

        masterGainRef.current = audioCtxRef.current.createGain();
        compressorRef.current = audioCtxRef.current.createDynamicsCompressor();
        filterRef.current = audioCtxRef.current.createBiquadFilter();

        masterGainRef.current.gain.value = 0.9;
        filterRef.current.type = "highpass";
        filterRef.current.frequency.value = 120;
        compressorRef.current.threshold.value = -24;
        compressorRef.current.knee.value = 30;
        compressorRef.current.ratio.value = 3;
        compressorRef.current.attack.value = 0.003;
        compressorRef.current.release.value = 0.25;

        // --- AEC INTEGRATION START ---
        const streamDest = audioCtxRef.current.createMediaStreamDestination();

        masterGainRef.current
          .connect(filterRef.current)
          .connect(compressorRef.current)
          .connect(streamDest);

        if (outputAudioElementRef.current) {
          outputAudioElementRef.current.srcObject = streamDest.stream;
          outputAudioElementRef.current
            .play()
            .catch((e) =>
              console.error("Error playing hidden audio element:", e),
            );
        }
        // --- AEC INTEGRATION END ---

        nextStartTimeRef.current = audioCtxRef.current.currentTime + 0.05;
      } catch (e) {
        console.error("Failed to create AudioContext:", e);
        setError("Web Audio API is not supported by this browser.");
      }
    }
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const stopAndClearPlayback = useCallback((reason = "unknown") => {
    try {
      if (currentAudioSourceRef.current && audioCtxRef.current) {
        try {
          const now = audioCtxRef.current.currentTime;
          currentAudioSourceRef.current.stop(now + 0.001);
          currentAudioSourceRef.current.disconnect();
        } catch (e) {
          // Source already stopped
        }
        currentAudioSourceRef.current = null;
      }

      if (masterGainRef.current && audioCtxRef.current) {
        try {
          const now = audioCtxRef.current.currentTime;
          masterGainRef.current.gain.cancelScheduledValues(now);
          masterGainRef.current.gain.setValueAtTime(
            masterGainRef.current.gain.value,
            now,
          );
          masterGainRef.current.gain.exponentialRampToValueAtTime(
            0.001,
            now + 0.01,
          );
        } catch (e) {
          // Gain fade error
        }
      }

      // --- AEC FIX: Clean up hidden audio element ---
      if (outputAudioElementRef.current) {
        outputAudioElementRef.current.pause();
        outputAudioElementRef.current.srcObject = null;
      }

      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.close().catch(() => {});
      }
    } catch (e) {
      console.error("Audio cleanup error:", e);
    }

    audioCtxRef.current = null;
    masterGainRef.current = null;
    compressorRef.current = null;
    filterRef.current = null;
    playbackQueueRef.current = [];
    bufferedDurationRef.current = 0;
    isPlayingRef.current = false;
    isAIRespondingRef.current = false;
    nextStartTimeRef.current = 0;
    setIsAIResponding(false);

    // --- WHITEBOARD CLEANUP ---
    pendingAnimationsRef.current = [];
    scheduledAnimationsRef.current = [];
    if (reason === "server_clear_command" || reason === "user_barge_in") {
      setBoardShape(null);
      setBoardText(null);
      setBoardHighlight(null);
      setBoardScene(null);
    }
  }, []);

  // --- VAD HELPER FUNCTIONS ---
  const calculateZCR = useCallback((audioData) => {
    let crossings = 0;
    for (let i = 1; i < audioData.length; i++) {
      if (audioData[i] >= 0 !== audioData[i - 1] >= 0) {
        crossings++;
      }
    }
    return crossings / audioData.length;
  }, []);

  const calculateSpectralRolloff = useCallback((audioData) => {
    const highFreqEnergy = audioData
      .slice(audioData.length / 2)
      .reduce((sum, val) => sum + Math.abs(val), 0);
    const totalEnergy = audioData.reduce((sum, val) => sum + Math.abs(val), 0);
    return totalEnergy > 0 ? highFreqEnergy / totalEnergy : 0;
  }, []);

  // --- IMPROVED VAD WITH DYNAMIC THRESHOLD ---
  const improvedDetectVoiceActivity = useCallback(
    (audioData) => {
      const {
        VOICE_ACTIVITY_THRESHOLD,
        SILENCE_DURATION_MS,
        ENERGY_CONSISTENCY_WINDOW,
        MIN_ENERGY_RATIO,
        SPECTRAL_ROLLOFF_THRESHOLD,
        ZCR_THRESHOLD,
        SPIKE_THRESHOLD,
        CONFIDENCE_THRESHOLD,
      } = IMPROVED_VAD_CONFIG;

      let sum = 0;
      let peak = 0;
      for (let i = 0; i < audioData.length; i++) {
        const val = audioData[i] * audioData[i];
        sum += val;
        if (val > peak) peak = val;
      }
      const rms = Math.sqrt(sum / audioData.length);

      // Spike Detection
      const peakRatio = rms > 0 ? Math.sqrt(peak) / rms : 0;
      if (peakRatio > SPIKE_THRESHOLD) {
        return;
      }

      // Dynamic background noise adaptation
      if (!voiceActivityRef.current && !isAIRespondingRef.current) {
        backgroundNoiseRef.current =
          backgroundNoiseRef.current * 0.98 + rms * 0.02;
      }

      const zcr = calculateZCR(audioData);
      const spectralRolloff = calculateSpectralRolloff(audioData);
      const dynamicThreshold = Math.max(
        VOICE_ACTIVITY_THRESHOLD,
        backgroundNoiseRef.current * 2,
      );

      // Criteria calculation
      const energyCriteria = rms > dynamicThreshold;
      const zcrCriteria = zcr < ZCR_THRESHOLD;
      const spectralCriteria = spectralRolloff < SPECTRAL_ROLLOFF_THRESHOLD;

      energyHistoryRef.current.push(rms > dynamicThreshold ? 1 : 0);
      if (energyHistoryRef.current.length > ENERGY_CONSISTENCY_WINDOW) {
        energyHistoryRef.current.shift();
      }
      const consistencyRatio =
        energyHistoryRef.current.reduce((s, v) => s + v, 0) /
        energyHistoryRef.current.length;
      const consistencyCriteria = consistencyRatio >= MIN_ENERGY_RATIO;

      // Confidence Score
      let confidence = 0;
      if (energyCriteria) confidence += 0.4;
      if (zcrCriteria) confidence += 0.25;
      if (spectralCriteria) confidence += 0.2;
      if (consistencyCriteria) confidence += 0.15;
      speechConfidenceRef.current = confidence;

      // --- AEC FIX: Dynamic confidence threshold ---
      let requiredConfidence = CONFIDENCE_THRESHOLD;
      if (isAIRespondingRef.current) {
        requiredConfidence = CONFIDENCE_THRESHOLD + 0.05;
      }

      const hasVoice = confidence >= requiredConfidence;

      if (hasVoice) {
        // --- BARGE-IN TRIGGER ---
        if (isAIRespondingRef.current && !voiceActivityRef.current) {
          console.log("[BARGE-IN] User interrupted AI response");
          stopAndClearPlayback("user_barge_in");
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            console.log("[WEBSOCKET SEND] Sending interrupt signal to server");
            socketRef.current.send(JSON.stringify({ type: "interrupt" }));
          }
        }

        if (!voiceActivityRef.current) {
          voiceActivityRef.current = true;

          // >>> NEW: Check and track usage per turn <<<
          (async () => {
            try {
              const usageAllowed = await checkUsageLimit();
              if (!usageAllowed) {
                console.log("Usage limit reached mid-session.");
                setError("Subscription limit reached. Session ending.");
                stopRecording(); // Shut down mic
                stopAndClearPlayback("limit_reached");
                if (socketRef.current) socketRef.current.close();
                return;
              }
              // Increment count for the new interaction turn
              await incrementUsage();
            } catch (err) {
              console.error("Error updating subscription usage:", err);
            }
          })();
          //--------End Check and track usage per turn-------------

          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          }
        }
      } else if (voiceActivityRef.current) {
        if (!silenceTimeoutRef.current) {
          silenceTimeoutRef.current = setTimeout(() => {
            voiceActivityRef.current = false;
            energyHistoryRef.current = [];
            silenceTimeoutRef.current = null;
          }, SILENCE_DURATION_MS);
        }
      }
    },
    [calculateZCR, calculateSpectralRolloff, stopAndClearPlayback],
  );

  const int16ToFloat32 = useCallback((int16) => {
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      const s = int16[i];
      float32[i] = s < 0 ? s / 32768.0 : s / 32767.0;
    }
    return float32;
  }, []);

  const processAndPlayQueue = useCallback(() => {
    const audioCtx = ensurePlaybackAudioContext();
    if (!audioCtx || playbackQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      return;
    }

    const maxSamplesToProcess = audioCtx.sampleRate * 1.5;
    const chunksToProcess = [];
    let totalSamples = 0;

    for (const chunk of playbackQueueRef.current) {
      if (totalSamples + chunk.samples.length > maxSamplesToProcess) break;
      chunksToProcess.push(chunk);
      totalSamples += chunk.samples.length;
    }

    if (chunksToProcess.length === 0) {
      return;
    }

    playbackQueueRef.current = playbackQueueRef.current.slice(
      chunksToProcess.length,
    );

    const consolidatedSamples = new Float32Array(totalSamples);
    let offset = 0;
    const tagSchedules = []; // { tags, sampleOffset } — where each sentence's audio starts

    for (const chunk of chunksToProcess) {
      if (chunk.tags && chunk.tags.length > 0) {
        tagSchedules.push({ tags: chunk.tags, sampleOffset: offset });
      }
      consolidatedSamples.set(chunk.samples, offset);
      offset += chunk.samples.length;
    }

    const sampleRate = chunksToProcess[0].sampleRate;
    bufferedDurationRef.current -= (totalSamples / sampleRate) * 1000;

    try {
      const buffer = audioCtx.createBuffer(
        1,
        consolidatedSamples.length,
        sampleRate,
      );
      buffer.copyToChannel(consolidatedSamples, 0, 0);

      const source = audioCtx.createBufferSource();
      source.buffer = buffer;
      source.connect(masterGainRef.current);
      currentAudioSourceRef.current = source;

      const now = audioCtx.currentTime;
      const startTime = Math.max(now, nextStartTimeRef.current);

      // --- WHITEBOARD SYNC: schedule each tag at the exact moment its own
      //     sentence's audio begins (offset within this consolidated buffer). ---
      tagSchedules.forEach(({ tags, sampleOffset }) => {
        const executeAt = startTime + sampleOffset / sampleRate;
        tags.forEach((tag) => {
          scheduledAnimationsRef.current.push({ tag, executeAt });
          console.log(
            `[SYNC] Scheduled ${tag} for playback at ${executeAt.toFixed(3)}s`,
          );
        });
      });

      source.start(startTime);
      nextStartTimeRef.current = startTime + buffer.duration;

      if (!isAIRespondingRef.current) {
        isAIRespondingRef.current = true;
        setIsAIResponding(true);
      }

      source.onended = () => {
        if (currentAudioSourceRef.current === source) {
          currentAudioSourceRef.current = null;
        }
        isPlayingRef.current = false;
        if (playbackQueueRef.current.length > 0) {
          setTimeout(() => processAndPlayQueue(), 5);
        } else {
          if (audioStreamEndedRef.current) {
            console.log("[WEBSOCKET SEND] Sending playback_finished to server");
            if (socketRef.current?.readyState === WebSocket.OPEN) {
              socketRef.current.send(
                JSON.stringify({ type: "playback_finished" }),
              );
            }
            audioStreamEndedRef.current = false;
          }
          endOfTurnTimerRef.current = setTimeout(() => {
            isAIRespondingRef.current = false;
            setIsAIResponding(false);
            endOfTurnTimerRef.current = null;
          }, 300);
        }
      };
    } catch (err) {
      console.error("Error scheduling audio buffer:", err);
      isPlayingRef.current = false;
      isAIRespondingRef.current = false;
      setIsAIResponding(false);
    }
  }, [ensurePlaybackAudioContext]);

  const handleIncomingAudio = useCallback(
    (arrayBuffer) => {
      if (endOfTurnTimerRef.current) {
        clearTimeout(endOfTurnTimerRef.current);
        endOfTurnTimerRef.current = null;
      }

      if (arrayBuffer.byteLength % 2 !== 0) {
        arrayBuffer = arrayBuffer.slice(0, arrayBuffer.byteLength - 1);
      }

      const int16 = new Int16Array(arrayBuffer);
      const float32 = int16ToFloat32(int16);
      const sampleRate = serverFormatRef.current.sampleRate;

      // Tags arrive just before their sentence's audio — attach any pending tags to
      // THIS chunk so the animation fires exactly when this sentence starts playing.
      const chunk = { samples: float32, sampleRate };
      if (pendingAnimationsRef.current.length > 0) {
        chunk.tags = pendingAnimationsRef.current;
        pendingAnimationsRef.current = [];
      }

      playbackQueueRef.current.push(chunk);

      bufferedDurationRef.current += (float32.length / sampleRate) * 1000;

      const targetBufferMs = PLAYBACK_BUFFER_TARGET_SECONDS * 1000;
      if (
        bufferedDurationRef.current >= targetBufferMs &&
        !isPlayingRef.current
      ) {
        isPlayingRef.current = true;
        processAndPlayQueue();
      }
    },
    [int16ToFloat32, processAndPlayQueue],
  );

  const connectWS = useCallback(
    (convId) => {
      return new Promise((resolve, reject) => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          resolve();
          return;
        }
        setConnectionStatus("connecting");
        const ws = new WebSocket(WS_URL);
        socketRef.current = ws;
        ws.binaryType = "arraybuffer";

        ws.onopen = () => {
          setConnectionStatus("connected");

          const classNumberMatch = String(className).match(/\d+/);
          const cleanClassNumber = classNumberMatch ? classNumberMatch[0] : "0";
          const formattedClassName = `cbse-${String(cleanClassNumber).padStart(
            2,
            "0",
          )}`;

          const startMessage = {
            type: "start",
            config: {
              conversation_id: convId,
              language_code: language,
              student_class: formattedClassName,
              subject_name: selectedSubject,
              chapter_name: selectedChapter,
            },
          };
          console.log("[WEBSOCKET SEND] Sending start message:", startMessage);
          ws.send(JSON.stringify(startMessage));
          resolve();
        };

        ws.onmessage = (event) => {
          if (typeof event.data === "string") {
            try {
              const parsed = JSON.parse(event.data);

              if (parsed?.type === "clear") {
                stopAndClearPlayback("server_clear_command");
                if (ws.readyState === WebSocket.OPEN) {
                  console.log("[WEBSOCKET SEND] Sending clear_ack to server");
                  ws.send(JSON.stringify({ type: "clear_ack" }));
                }
                return;
              } else if (parsed?.type === "audio_stream_end") {
                audioStreamEndedRef.current = true;
                if (
                  !isPlayingRef.current &&
                  playbackQueueRef.current.length === 0
                ) {
                  console.log(
                    "[WEBSOCKET SEND] Sending playback_finished to server",
                  );
                  if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ type: "playback_finished" }));
                  }
                }
                return;
              }

              // --- WHITEBOARD INTERCEPTION ---
              if (parsed?.type === "animation") {
                console.log(`[SYNC] Received animation tag: ${parsed.tag}`);
                pendingAnimationsRef.current.push(parsed.tag);
                return;
              }

              if (parsed?.type === "format") {
                const newSampleRate =
                  parsed.sampleRate || DEFAULT_TTS_SAMPLE_RATE;
                serverFormatRef.current = {
                  ...serverFormatRef.current,
                  ...parsed,
                  sampleRate: newSampleRate,
                };
              } else if (parsed?.type === "response_end") {
                isAIRespondingRef.current = false;
                setIsAIResponding(false);
              }
            } catch (e) {
              console.error("Failed to parse server JSON:", e);
            }
          } else if (event.data instanceof ArrayBuffer) {
            if (audioCtxRef.current || ensurePlaybackAudioContext()) {
              handleIncomingAudio(event.data);
            }
          }
        };

        ws.onerror = (err) => {
          console.error("WebSocket error:", err);
          setConnectionStatus("error");
          setError("WebSocket connection failed.");
          reject(err);
        };

        ws.onclose = () => {
          setConnectionStatus("disconnected");
          setIsRecording(false);
          stopAndClearPlayback("websocket_close");
        };
      });
    },
    [
      language,
      className,
      selectedSubject,
      selectedChapter,
      handleIncomingAudio,
      stopAndClearPlayback,
      ensurePlaybackAudioContext,
    ],
  );

  const startRecording = useCallback(async () => {
    if (isRecording) {
      return;
    }
    setError(null);
    stopAndClearPlayback("user_barge_in");
    setIsMuted(false); // Ensure mic is unmuted when starting

    try {
      const newConversationId = new ObjectId().toHexString();
      await connectWS(newConversationId);

      const audioCtx =
        inputAudioContextRef.current ||
        new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: REQUESTED_UPLINK_SAMPLE_RATE,
        });
      inputAudioContextRef.current = audioCtx;

      const inputWorkletCode = `
        class AudioProcessor extends AudioWorkletProcessor {
          constructor(options) {
            super();
            this.bufferSize = 256;
            this._buffer = new Float32Array(0);
            
            // High-pass filter coefficients for ~400Hz cutoff at 16kHz sample rate
            this.b0 = 0.9511;
            this.b1 = -1.9022;
            this.b2 = 0.9511;
            this.a1 = -1.9008;
            this.a2 = 0.9048;
            this.x1 = 0; this.x2 = 0;
            this.y1 = 0; this.y2 = 0;
          }
        
          applyFilter(inputSample) {
            const y = this.b0 * inputSample + this.b1 * this.x1 + this.b2 * this.x2 - this.a1 * this.y1 - this.a2 * this.y2;
            this.x2 = this.x1;
            this.x1 = inputSample;
            this.y2 = this.y1;
            this.y1 = y;
            return y;
          }

          process(inputs) {
            const channel = inputs[0]?.[0];
            if (!channel) return true;
            
            const filteredChannel = new Float32Array(channel.length);
            for (let i = 0; i < channel.length; i++) {
                filteredChannel[i] = this.applyFilter(channel[i]);
            }
            
            this.port.postMessage({ type: 'voice_activity_data', audioData: Array.from(filteredChannel) });
            
            const newBuf = new Float32Array(this._buffer.length + filteredChannel.length);
            newBuf.set(this._buffer, 0);
            newBuf.set(filteredChannel, this._buffer.length);
            this._buffer = newBuf;
            
            while (this._buffer.length >= this.bufferSize) {
              const chunk = this._buffer.subarray(0, this.bufferSize);
              const int16 = new Int16Array(chunk.length);
              for (let i = 0; i < chunk.length; i++) {
                let s = Math.max(-1, Math.min(1, chunk[i]));
                int16[i] = s < 0 ? s * 32768 : s * 32767;
              }
              this.port.postMessage({ type: 'audio_data', buffer: int16.buffer }, [int16.buffer]);
              this._buffer = this._buffer.subarray(this.bufferSize);
            }
            return true;
          }
        }
        registerProcessor('audio-processor', AudioProcessor);
      `;

      if (audioCtx.audioWorklet.addModule) {
        await audioCtx.audioWorklet.addModule(
          URL.createObjectURL(
            new Blob([inputWorkletCode], { type: "application/javascript" }),
          ),
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: REQUESTED_UPLINK_SAMPLE_RATE,
          autoGainControl: false,
        },
      });
      streamRef.current = stream;

      const source = audioCtx.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioCtx, "audio-processor");

      workletNode.port.onmessage = (event) => {
        if (event.data.type === "voice_activity_data") {
          improvedDetectVoiceActivity(event.data.audioData);
        } else if (event.data.type === "audio_data") {
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            console.log("[WEBSOCKET SEND] Sending audio data to server");
            socketRef.current.send(event.data.buffer);
          }
        }
      };

      source.connect(workletNode);
      inputAudioWorkletNodeRef.current = workletNode;
      setIsRecording(true);

      if (audioCtx.state === "suspended") {
        await audioCtx.resume();
      }
    } catch (err) {
      console.error("Failed to start recording:", err);
      setError(
        err.message || "Could not start recording. Check mic permissions.",
      );
      setIsRecording(false);
    }
  }, [
    isRecording,
    connectWS,
    improvedDetectVoiceActivity,
    stopAndClearPlayback,
  ]);

  const stopRecording = useCallback(() => {
    if (!isRecording) {
      return;
    }
    setIsRecording(false);
    setIsMuted(false); // Reset mute state when stopped

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      console.log("[WEBSOCKET SEND] Sending stop message to server");
      socketRef.current.send(JSON.stringify({ type: "stop" }));
    }

    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    inputAudioWorkletNodeRef.current?.disconnect();
    inputAudioWorkletNodeRef.current = null;

    if (
      inputAudioContextRef.current &&
      inputAudioContextRef.current.state !== "closed"
    ) {
      inputAudioContextRef.current.close().then(() => {
        inputAudioContextRef.current = null;
      });
    }

    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
  }, [isRecording]);

  useEffect(() => {
    return () => {
      if (isRecording) stopRecording();
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.close();
      }
    };
  }, [isRecording, stopRecording]);

  //Function to toggle microphone mute
  const toggleMute = () => {
    if (streamRef.current) {
      const audioTracks = streamRef.current.getAudioTracks();
      let currentlyMuted = isMuted;
      audioTracks.forEach((track) => {
        track.enabled = !track.enabled; // Toggle track state
        currentlyMuted = !track.enabled;
      });
      setIsMuted(currentlyMuted);
    }
  };

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      setIsModalOpen(true);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!className || !selectedSubject || !selectedChapter) {
      console.log("Please fill in all required fields");
      return;
    }

    setIsModalOpen(false);
    startRecording();
  };

  const viewVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.3, ease: "easeIn" },
    },
  };

  const renderLoadingView = () => {
    return (
      <motion.div
        key="loading-view"
        variants={viewVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="flex flex-col items-center"
      >
        <div className="mb-12 relative">
          <motion.div
            className="absolute inset-0 w-32 h-32 md:w-40 md:h-40 border-4 border-blue-400 rounded-full border-t-transparent mx-auto"
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          />
          <div className="relative w-32 h-32 md:w-40 md:h-40 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-2xl">
            <span className="text-4xl md:text-5xl">🧠</span>
          </div>
        </div>
        <div className="mb-8 h-8 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={loadingMessageIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-xl md:text-2xl text-gray-400 font-medium"
            >
              {processingMessages[loadingMessageIndex]}
            </motion.p>
          </AnimatePresence>
        </div>
      </motion.div>
    );
  };

  const renderInteractiveView = () => {
    return (
      <motion.div
        key="interactive-view"
        variants={viewVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="relative flex flex-col md:flex-row items-center justify-center h-full w-full p-4 gap-8 md:gap-16"
      >
        {/* --- LEFT SIDE: MICROPHONE SECTION --- */}
        <div className="flex flex-col items-center justify-center w-full md:w-1/2 flex-shrink-0">
          <motion.div className="relative flex items-center justify-center">
            {(isAIResponding ||
              (!isRecording && connectionStatus === "disconnected")) && (
              <motion.div
                className="absolute bg-[#3B82F6] rounded-full"
                animate={{ scale: [1, 1.5, 1], opacity: [0.6, 0, 0.6] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{ width: "120px", height: "120px" }}
              />
            )}

            <motion.button
              className={`relative w-24 h-24 rounded-full flex items-center justify-center text-5xl cursor-pointer shadow-xl transition-colors duration-300 ${
                isRecording
                  ? "bg-red-600"
                  : "bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6]"
              }`}
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.9 }}
              disabled={connectionStatus === "connecting"}
              onClick={handleMicClick}
              animate={isRecording ? { scale: [1, 1.05, 1] } : {}}
              transition={
                isRecording
                  ? { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
                  : {}
              }
            >
              {isRecording ? (
                <div className="flex items-center justify-center space-x-1.5">
                  {[...Array(5)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-1.5 bg-white rounded-full shadow-lg"
                      animate={{
                        height: [16, 48, 16],
                        opacity: [0.6, 1, 0.6],
                      }}
                      transition={{
                        duration: 0.6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: i * 0.08,
                      }}
                    />
                  ))}
                </div>
              ) : (
                "🎙️"
              )}
            </motion.button>
          </motion.div>

          <div className="mt-10 text-xl text-slate-300 h-12 flex flex-col items-center justify-center text-center">
            {error ? (
              <span className="text-red-400">{error}</span>
            ) : connectionStatus === "connecting" ? (
              t("voicebot.connecting")
            ) : isRecording ? (
              <div className="flex flex-col items-center space-y-1">
                <span>{t("voicebot.clickToStop")}</span>
              </div>
            ) : isAIResponding ? (
              t("voicebot.aiResponding")
            ) : (
              t("voicebot.clickToStart")
            )}
          </div>

          {/* NEW: Mute Button UI */}
          {isRecording && (
            <motion.button
              onClick={toggleMute}
              className={`mt-6 px-6 py-2.5 rounded-full font-semibold shadow-lg transition-all border flex items-center justify-center space-x-2 ${
                isMuted 
                  ? "bg-red-500/20 text-red-400 border-red-500/50 hover:bg-red-500/30" 
                  : "bg-gray-800/50 text-gray-300 border-gray-600 hover:bg-gray-700"
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>{isMuted ? "🔇" : "🔊"}</span>
              <span>{isMuted ? "Unmute Mic" : "Mute Mic"}</span>
            </motion.button>
          )}
          
        </div>

        {/* --- RIGHT SIDE: WHITEBOARD VISUALIZER --- */}
        <div className="w-full md:w-1/2 max-w-lg bg-white border border-slate-200 p-6 rounded-xl text-center z-20 shadow-2xl min-h-[300px] lg:min-h-[400px] flex flex-col justify-center flex-shrink-0">
          <h3 className="text-slate-400 text-sm font-semibold mb-4 uppercase tracking-wide">
            Whiteboard
          </h3>
          <div className="flex-grow flex items-center justify-center">
            {boardScene || boardShape || boardText ? (
              <div className="flex flex-col items-center gap-3">
                {boardScene && <SceneRenderer key="scene" spec={boardScene} />}
                {boardShape && !boardScene && (
                  <DrawnShape
                    key={`shape-${boardShape}`}
                    object={boardShape}
                    highlight={boardHighlight}
                  />
                )}
                {boardText && (
                  <HandwrittenText key={`text-${boardText}`} text={boardText} />
                )}
              </div>
            ) : (
              <p className="text-slate-300 text-sm italic">waiting…</p>
            )}
          </div>
        </div>

        {/* --- SETTINGS MODAL --- */}
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              onClick={() => !isRecording && setIsModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", damping: 15 }}
                className="bg-[#20398a] w-full max-w-sm p-6 rounded-xl shadow-2xl border border-slate-700 max-h-screen overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4 pt-2">
                    <div>
                      <label
                        htmlFor="language-select"
                        className="block text-sm font-medium text-gray-300 mb-1 text-left"
                      >
                        {t("voicebot.language")}
                      </label>
                      <select
                        id="language-select"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        disabled={isRecording}
                        className="w-full px-3 py-2 text-base text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200 disabled:cursor-not-allowed"
                      >
                        <option value="en-US">{t("voicebot.english")}</option>
                        <option value="hi-IN">{t("voicebot.hindi")}</option>
                        <option value="te-IN">{t("voicebot.telugu")}</option>
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="class-name"
                        className="block text-sm font-medium text-gray-300 mb-1 text-left"
                      >
                        {t("voicebot.className")}
                      </label>
                      <div
                        id="class-name"
                        className="w-full px-3 py-2 text-base text-gray-900 bg-gray-100 border border-gray-300 rounded-lg shadow-sm text-left"
                      >
                        {className}
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="subject-select"
                        className="block text-sm font-medium text-gray-300 mb-1 text-left"
                      >
                        {t("voicebot.subject")}
                      </label>
                      <select
                        id="subject-select"
                        value={selectedSubject}
                        onChange={(e) => {
                          setSelectedSubject(e.target.value);
                          setSelectedChapter("");
                        }}
                        className="w-full px-3 py-2 text-base text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200 disabled:cursor-not-allowed"
                        disabled={subjects.length === 0}
                      >
                        <option value="">
                          {subjects.length === 0
                            ? t("voicebot.loadingSubjects")
                            : t("voicebot.selectSubject")}
                        </option>
                        {subjects.map((subject) => (
                          <option key={subject.id} value={subject.id}>
                            {subject.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="chapter-select"
                        className="block text-sm font-medium text-gray-300 mb-1 text-left"
                      >
                        {t("voicebot.chapter")}
                      </label>
                      <select
                        id="chapter-select"
                        value={selectedChapter}
                        onChange={(e) => setSelectedChapter(e.target.value)}
                        disabled={!selectedSubject || chapters.length === 0}
                        className="w-full px-3 py-2 text-base text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200 disabled:cursor-not-allowed"
                      >
                        <option value="">
                          {!selectedSubject
                            ? t("voicebot.selectSubjectFirst")
                            : chapters.length === 0
                              ? t("voicebot.noChapters")
                              : t("voicebot.selectChapter")}
                        </option>
                        {chapters.map((chapter, idx) => (
                          <option key={idx} value={chapter}>
                            {chapter}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="mt-6 w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-800 disabled:cursor-not-allowed"
                    disabled={
                      isRecording || !selectedChapter || !selectedSubject
                    }
                  >
                    {t("voicebot.startSession")}
                  </button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 px-4 text-center text-white overflow-hidden">
      {/* Top Left Back Button Box */}
      <div className="absolute top-6 left-6 z-50">
        <button
          onClick={() => navigate("/dashboard/student/chatbot")}
          className="flex items-center justify-center p-2.5 bg-gray-800/40 hover:bg-gray-700/60 border border-gray-500/30 rounded-lg backdrop-blur-md transition-all text-gray-300 hover:text-white shadow-lg"
          title="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute bg-blue-400 rounded-full opacity-20"
            style={{
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              x: [0, Math.random() * 100 - 50, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>

      {/* UPDATE IS HERE: Changed max-w-2xl to max-w-5xl for side-by-side layout */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-5xl h-full justify-center">
        <AnimatePresence mode="wait">
          {isLoading ? renderLoadingView() : renderInteractiveView()}
        </AnimatePresence>
      </div>

      {/* AEC FIX: Hidden Audio Element for Echo Cancellation */}
      <audio
        ref={outputAudioElementRef}
        style={{ display: "none" }}
        autoPlay
        playsInline
      />
    </div>
  );
};

export default AITutorPage;
